import React, { useEffect, useRef } from "react";
import { Row, Col, Space } from "antd";

// components
import ClassList from "common/class_card/ClassList";
import BookingModal from "common/modal/booking_modal";
import NoBookedGroupModal from "common/modal/booking_modal/NoBookedGroupModal";
import PastClassModal from "common/modal/booking_modal/PastClassModal";
import BookedClassModal from "common/modal/booking_modal/BookedClassModal";
import ScheduleConflictModal from "common/modal/schedule_conflict_modal";

// hooks
import { useTranslation } from "react-i18next";
import useModal from "hooks/useModal";
import useCountdown from "hooks/useCountdown";

// utils
import { Lesson } from "types";
import { BannerInstruction, BookClassButton } from "./styles";
import ClassParticipants from "./ClassParticipants";

type TProps = {
  currentClass: Lesson;
  setButtonRef: (width: number) => void;
};

const BookThisClass = ({ currentClass, setButtonRef }: TProps) => {
  const timeLeft = useCountdown(currentClass?.startTime, false);
  const { t } = useTranslation("class_banner");
  const [bookedModalVisible, showBooked, closeBooked] = useModal();
  const [scheduleConflictVisible, showConflict, closeConflict] = useModal();
  const [bookingModalVisible, showBooking, closeBooking] = useModal();
  const [pastClassModalVisible, showPast, closePast] = useModal();
  const [noBookedModalVisible, showNoBooked, closeNoBooked] = useModal();

  const handleClickCardDetails = () => {
    if (currentClass?.bookedAt) {
      showBooked();
      return;
    }
    if (currentClass?.overlapped) {
      showConflict();
      return;
    }

    const isInThePast = timeLeft.totalSecondsLeft <= 0;
    if (isInThePast) {
      showPast();
      return;
    }
    const fourtyEightHoursToSecond = 60 * 60 * 48;
    const isEmptyAndLessThan48Hour =
      timeLeft.totalSecondsLeft < fourtyEightHoursToSecond &&
      currentClass.seatsRemaining === currentClass.classSize &&
      currentClass.classType.toLowerCase() === "small group";
    if (!isInThePast && isEmptyAndLessThan48Hour) {
      showNoBooked();
      return;
    }
    showBooking();
  };
  const renderModals = (
    <>
      <BookedClassModal
        visible={bookedModalVisible}
        onCloseModal={() => closeBooked()}
        classType={currentClass.classType}
      />
      <ScheduleConflictModal
        visible={scheduleConflictVisible}
        onCloseModal={() => closeConflict()}
      />
      <PastClassModal
        visible={pastClassModalVisible}
        onCloseModal={() => closePast()}
      />
      <NoBookedGroupModal
        visible={noBookedModalVisible}
        onCloseModal={() => closeNoBooked()}
      />

      <BookingModal
        visible={bookingModalVisible}
        onCloseModal={() => closeBooking()}
        state={{
          classId: currentClass?.id,
          classType: currentClass?.classType,
          filteredLanguageId: currentClass?.language?.id,
          classCredit: currentClass?.credits,
        }}
      />
    </>
  );

  const bookClassButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bookClassButtonRef.current) {
      const width = bookClassButtonRef.current
        ? bookClassButtonRef.current.offsetWidth
        : 0;
      setButtonRef(width);
    }
  }, [setButtonRef]);

  return (
    <Row gutter={24}>
      <ClassList lessons={[currentClass]} hidesFooter />
      <Col xs={24} sm={24} md={8} lg={12}>
        <ClassParticipants
          participants={currentClass.participants}
          className="participants"
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Space direction="vertical">
          <div
            style={{
              marginTop: 8,
              display: "inline-flex",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <BookClassButton
              type="primary"
              onClick={() => handleClickCardDetails()}
              ref={bookClassButtonRef}
            >
              {t("BOOK CLASS")}
            </BookClassButton>
          </div>
          <BannerInstruction>
            {t(
              "You can cancel any class up to {{ hour }} hours beforehand to get your credits fully credited back to you.",
              { hour: 24 }
            )}
          </BannerInstruction>
        </Space>
      </Col>
      {renderModals}
    </Row>
  );
};

export default BookThisClass;
